import { Chat, ExamSession, ExamTicket, Message, OralCorrection, PracticeSession, TodoItem, TodoList, User, UserLangProgress, Vocab } from '@/components/Types';
import { getCurrentBearerToken } from '@/lib/client-auth';
import { isBlank } from '@/lib/utils';
import { setAchievementRewards, setCorrections, setCurrentExam, setCurrentExamId, setCurrentPractice, setCurrentPracticeId, setCurrentTutor, setEnableNewUi, setNewTodoItemCreated, setTodoList, setVocab } from '@/store/reducers/appSlice';
import { Dispatch } from 'redux';
import { api, EditableUserPreferences, frontendApi } from '@/lib/api-requests';
import { setCurrentUser } from '@/store/reducers/appSlice';
import { setLanguageProgress } from '@/store/reducers/appSlice';
import { setExamSession } from '../reducers/examSlice';
import { setAgent, setChatId, setCurrentChat } from '../reducers/chatSlice';
import { LANGUAGES } from '@/lib/constants';
import { store } from '../store';
import { todoListToolsUi } from '@/lib/todo/todo-list-tools-ui';

export async function loadCurrentUser(dispatch: Dispatch) : Promise<User | null> {
    const user = await api.loadCurrentUser();
    if (user != null && dispatch != null) {
        dispatch(setCurrentUser(user));

        // While the new UI mode is in beta, we only allow access to the new UI for admins and Spanish users
        dispatch(setEnableNewUi(['spanish', 'german'].includes(user.learningLanguage as string)));
    }
    return user as User | null;
}

export async function loadCurrentChat(dispatch: Dispatch, id: string) : Promise<Chat | null> {
    const chat = await frontendApi.chat.getChat(id);
    if (chat != null && dispatch != null) {
        dispatch(setCurrentChat(chat));
        dispatch(setChatId(chat._id));
    }
    return chat as Chat | null;
}

export async function loadCurrentExamSession(dispatch: Dispatch, id: string) : Promise<ExamSession | null> {
    const examSession = await api.loadExamSession(id);
    if (examSession != null && dispatch != null) {
        dispatch(setCurrentExam(examSession));
        dispatch(setExamSession(examSession)); // newer code for the dedicated exam reducer
        dispatch(setCurrentExamId(id));
    }
    return examSession as ExamSession | null;
}

export async function createCurrentExamSession(dispatch: Dispatch, payload: { userId: string, motherLanguage: string, targetLanguage: string, userLevel: string, examStage: string, examType: string, examTicket?: ExamTicket | null, examId?: string | null }): Promise<ExamSession | null> {
    const examSession = await api.createExamSession(payload);
    if (examSession != null && dispatch != null) {
        dispatch(setCurrentExam(examSession));
        dispatch(setExamSession(examSession)); // newer code for the dedicated exam reducer
        dispatch(setCurrentExamId(examSession._id));
    }
    return examSession as ExamSession | null;
}

export async function loadCurrentPracticeSession(dispatch: Dispatch, id: string) : Promise<PracticeSession | null> {
    const practiceSession = await api.loadPracticeSession(id);
    if (practiceSession != null && dispatch != null) {
        dispatch(setCurrentPractice(practiceSession));
        dispatch(setCurrentPracticeId(id));
    }
    return practiceSession as PracticeSession | null;
}

export async function createCurrentPracticeSession(dispatch: Dispatch, payload: { userId: string, motherLanguage: string, targetLanguage: string, userLevel: string }): Promise<PracticeSession | null> {
    const practiceSession = await api.createPracticeSession(payload);
    if (practiceSession != null && dispatch != null) {
        dispatch(setCurrentPractice(practiceSession));
        dispatch(setCurrentPracticeId(practiceSession._id));
    }
    return practiceSession as PracticeSession | null;
}

export async function setPracticeSessionCompleted(dispatch: Dispatch, id: string, messages: Message[], vocab: Vocab[], corrections: OralCorrection[]) {
    const practiceSession = await api.completePracticeSession(id, messages, vocab, corrections);
    if (practiceSession != null && dispatch != null) {
        dispatch(setCurrentPractice(practiceSession));
    }
}

export async function setExamSessionCompleted(dispatch: Dispatch, id: string, messages: Message[], vocab: Vocab[], corrections: OralCorrection[]) {
    const examSession = await api.completeExamSession(id, messages, vocab, corrections);
    if (examSession != null && dispatch != null) {
        dispatch(setCurrentExam(examSession));
    }
}

export async function loadCurrentUserLangProgress(dispatch: Dispatch, language: string, skipCorrections: boolean = false, skipVocab: boolean = false) : Promise<UserLangProgress | null> {
    // Get user from the state
    const userLangProgress = await api.loadUserLangProgress(language);
    
    if (userLangProgress != null && dispatch != null) {
        dispatch(setLanguageProgress(userLangProgress));
        
        if (!skipCorrections) {
            await loadCurrentUserLangProgressCorrections(dispatch, language);
        }

        // if (!skipVocab) {
        //     await loadCurrentUserLangProgressVocab(dispatch, language);
        // }
    }

    return userLangProgress as UserLangProgress | null;
}

export async function loadCurrentUserLangProgressVocab(dispatch: Dispatch, language: string) {
    const result = await api.loadUserLangProgressVocab(language);
    if (result != null && dispatch != null) {
        dispatch(setVocab(result));
    }
}

export async function loadCurrentUserLangProgressCorrections(dispatch: Dispatch, language: string) {
    const result = await api.loadUserLangProgressCorrections(language);
    if (result != null && dispatch != null) {
        dispatch(setCorrections(result));
    }
}

export async function loadCurrentTutor(dispatch: Dispatch, language: string, level: string) {
    const result = await api.loadTutors(language, level);
    if (result != null && result.length > 0 && dispatch != null) {
        dispatch(setCurrentTutor(result[0]));
    }
}

export async function loadCurrentTutorById(dispatch: Dispatch, tutorId: string) {
    const result = await frontendApi.tutors.loadTutor(tutorId);
    if (result != null) {
        dispatch(setCurrentTutor(result));
    }
}

export async function loadCurrentExamSessionTutor(dispatch: Dispatch, language: string, level: string, examType: string, examStage: string) {
    const result = await api.loadTutors(language, level, examStage);
    if (result != null && result.length > 0 && dispatch != null) {
        dispatch(setCurrentTutor(result[0]));
    }
}

export async function updateLearningLanguage(dispatch: Dispatch, language: string, level: string) {
    const result = await api.updateUserPreferences({
        learningLanguage: language,
        userLevel: level
    });

    if (result != null && dispatch != null) {
        dispatch(setCurrentUser(result.user));
        dispatch(setLanguageProgress(result.userLangProgress));

        await loadCurrentUserLangProgressCorrections(dispatch, language);
        // await loadCurrentUserLangProgressVocab(dispatch, language);
    }
}

export async function updateUserPreference(dispatch: Dispatch, key: string, value: any) {
    try {
        const result = await api.updateUserPreferences({
            [key]: value
        });

        if (result != null && result.user != null && dispatch != null) {
            dispatch(setCurrentUser(result.user));
        }
    } catch (error) {
        console.error(error);
    }
}
    
export async function updateEditableUserPreferences(dispatch: Dispatch, preferences: EditableUserPreferences) {
    try {
        const result = await api.updateUserPreferences(preferences);

        if (result != null && result.user != null && dispatch != null) {
            dispatch(setCurrentUser(result.user));
        }
    } catch (error) {
        console.error(error);
    }
}

// Deprecated
export async function markVocabLearned(dispatch: Dispatch, language: string, word: string) {
    await api.markVocabLearned(language, word);
    if (dispatch != null) {
        await loadCurrentUserLangProgressVocab(dispatch, language);
    }
}

// Deprecated
export async function markVocabDismissed(dispatch: Dispatch, language: string, word: string) {
    await api.markVocabDismissed(language, word);
    if (dispatch != null) {
        await loadCurrentUserLangProgressVocab(dispatch, language);
    }
}

export async function markCorrectionDismissed(dispatch: Dispatch, language: string, correction: OralCorrection) {
    await api.markCorrectionDismissed(language, correction);
    if (dispatch != null) {
        await loadCurrentUserLangProgressCorrections(dispatch, language);
    }
}

export async function loadTodoList(dispatch: Dispatch, tag: string, language: keyof typeof LANGUAGES): Promise<TodoList | null> {
    const todoList = await frontendApi.todoLists.getTodoList(tag, language);

    if (todoList != null && dispatch != null) {
        dispatch(setTodoList({tag, todoList}));

        // Check if the todo list has any new items, for which there was no local notification shown yet
        // We'll only show a notification for the latest todo item
        const candidateItems: TodoItem[] = []; 

        const now = new Date();
        const notificationShownThreshold = now.getTime() - 1000 * 60 * 60 * 24; // 24 hours

        for (const item of todoList.items) {
            if (!item.completed) {        
                // Skip expired
                if (item.expiresAt) {
                    const expirationTime = new Date(item.expiresAt);
                    if (expirationTime < now) {
                        continue;
                    }
                }

                // Check if the item was created recently enough
                const creationTime = new Date(item.createdAt);

                // Skip items created too long ago
                if (creationTime.getTime() < notificationShownThreshold) {
                    continue;
                }

                // Skip items without a characterId
                if (!item.chatConfig?.characterId) {
                    continue;
                }
                        
                candidateItems.push(item);
            }
        }

        // Get the most recently created item
        let todoNotificationShown = false;
        const latestItem = candidateItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        if (latestItem != null) {
            if (!todoListToolsUi.getLocalNotificationCreated(latestItem._id.toString())) {
                // Also, emit a local todo-created event
                dispatch(setNewTodoItemCreated(latestItem));
            }
        }

        if (!todoNotificationShown && todoList && todoList.items && todoList.items.length > 0) {
            // If there's nothing to show, we can purge the local cache, since it's not relevant
            todoListToolsUi.purgeLocalNotificationsCreatedCache();
        }
    }

    return todoList as TodoList | null;
}

export async function addContact(dispatch: Dispatch, contactName: string, language: string) {
    const result = await frontendApi.contacts.addContact(contactName, language);

    if (result != null && result.success && dispatch != null) {
        // Update the langProgress in the store with the new contacts
        const langProgress = store.getState().app.languageProgress as UserLangProgress;

        if (langProgress != null) {
            dispatch(setLanguageProgress({...langProgress, contacts: result.contacts}));
        }
    }
}

export async function loadAchievementRewards(dispatch: Dispatch, language: string) {
    const results = await frontendApi.user.getUserRewards(language);
    
    if (results != null && dispatch != null) {
        dispatch(setAchievementRewards(results));
    }
}